import { useRef, useState } from 'react';
import type { ReactElement } from 'react';

import { BpkDropdownChip } from '@skyscanner/backpack-web/bpk-component-chip';
import BpkPopover from '@skyscanner/backpack-web/bpk-component-popover';
import type { BpkPopoverProps } from '@skyscanner/backpack-web/bpk-component-popover';

type Props = {
  id: string;
  displayText: string;
  label: string;
  accessibilityLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  children: ReactElement;
  popoverProps?: Omit<
    BpkPopoverProps,
    'id' | 'label' | 'onClose' | 'isOpen' | 'target' | 'children'
  >;
  onClick?: () => void;
  usePropagationToClose?: boolean;
};

const DropdownPopover = ({
  accessibilityLabel,
  children,
  disabled,
  displayText,
  id,
  label,
  onClick,
  popoverProps,
  selected,
  usePropagationToClose = true,
}: Props) => {
  const target = useRef(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleChipClick = () => {
    setIsPopoverOpen(true);
    onClick?.();
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handlePropagationClick = () => {
    if (usePropagationToClose) {
      setIsPopoverOpen(false);
    }
  };

  return (
    <BpkPopover
      id={id}
      label={label}
      onClose={handlePopoverClose}
      isOpen={isPopoverOpen}
      target={
        <div ref={target}>
          <BpkDropdownChip
            accessibilityLabel={accessibilityLabel ?? displayText}
            onClick={handleChipClick}
            selected={selected}
            disabled={disabled}
            role="button"
            aria-haspopup="listbox"
            data-test-id={id}
          >
            {displayText}
          </BpkDropdownChip>
        </div>
      }
      padded={false}
      {...popoverProps}
    >
      <div
        role="presentation"
        onKeyUp={() => {}}
        onClick={handlePropagationClick}
      >
        {children}
      </div>
    </BpkPopover>
  );
};

export default DropdownPopover;
